/**
 * Phase 10D: Install prompt shown on 2nd visit. Feature-flag and dismiss (30 days).
 */
import React, { useState, useEffect } from 'react';
import { Snackbar, Button, Paper, Typography, Box } from '@mui/material';
import { trackPWAInstallPromptShown, trackPWAInstalled } from '../utils/analytics';
import { FEATURES } from '../config/features';

const DISMISS_STORAGE_KEY = 'pwa_prompt_dismissed';
const DISMISS_DAYS = 30;
const VISIT_COUNT_KEY = 'renewals_visit_count';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    if (!FEATURES.pwaInstallPrompt || isInstalled || !deferredPrompt) return;

    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    localStorage.setItem(VISIT_COUNT_KEY, String(count + 1));

    const dismissed = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissed) {
      const then = parseInt(dismissed, 10);
      if (Date.now() - then < DISMISS_DAYS * 86400000) return;
    }

    if (count === 2) {
      trackPWAInstallPromptShown({ visitCount: 2 });
      setShowPrompt(true);
    }
  }, [deferredPrompt, isInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    trackPWAInstallPromptShown({ visitCount: 2 });
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      trackPWAInstalled({ visitCount: 2 });
      localStorage.setItem('pwa_installed', 'true');
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  };

  return (
    <Snackbar open={showPrompt} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleDismiss}>
      <Paper sx={{ p: 2, minWidth: 320 }}>
        <Typography variant="subtitle1">Install Tutamen App</Typography>
        <Typography variant="body2" color="text.secondary">Quick access to renewals. Works offline.</Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" onClick={handleInstall}>Install</Button>
          <Button size="small" onClick={handleDismiss}>Not Now</Button>
        </Box>
      </Paper>
    </Snackbar>
  );
}
